import { Anchor, Check, History, SquarePen, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useApi } from '../../api/useApi';
import { AssistantPanel } from '../AssistantPanel';
import { useAssistant } from './AssistantProvider';

/** Shared chrome for the three icon-only header controls (40x40 tap target). */
const HEADER_BUTTON_CLASS =
  'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-40';

/** Render a compact relative date for the conversation menu. */
function relativeDate(value: string): string {
  const time = Date.parse(value);
  if (Number.isNaN(time)) return '';
  const diffMs = Date.now() - time;
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks === 1) return '1 week ago';
  return `${diffWeeks} weeks ago`;
}

/** Check glyph marking the conversation the dock is currently showing. */
function ActiveConversationMarker({ active }: { active: boolean }) {
  if (!active) return null;
  return <Check aria-hidden="true" className="size-4 shrink-0 text-primary" />;
}

/** Right-side global assistant dock. */
export function AssistantDock() {
  const api = useApi();
  const {
    open,
    activeConversation,
    recentConversations,
    groundingResearchId,
    pageContext,
    seed,
    storageScope,
    close,
    newConversation,
    switchConversation,
    refreshConversations,
  } = useAssistant();
  const [recentOpen, setRecentOpen] = useState(false);
  const recentContainerRef = useRef<HTMLDivElement>(null);
  const recentTriggerRef = useRef<HTMLButtonElement>(null);
  const recentMenuRef = useRef<HTMLDivElement>(null);
  const titleCache = useRef(new Map<string, string>());
  const [groundingTitle, setGroundingTitle] = useState<string | null>(null);
  // The panel owns the live transcript; persisted state can lag it by the save
  // debounce, or never arrive at all when there is no storage scope.
  const [liveMessages, setLiveMessages] = useState<{
    conversationId: string;
    count: number;
  } | null>(null);

  const onPanelMessagesChanged = useCallback((conversationId: string, count: number) => {
    setLiveMessages((current) => {
      if (current && current.conversationId === conversationId && current.count === count) {
        return current;
      }
      return { conversationId, count };
    });
  }, []);

  useEffect(() => {
    if (!open) setRecentOpen(false);
  }, [open]);

  // Opening moves focus into the menu. Conversations hydrate asynchronously
  // (the storage scope resolves after /api/me), so a menu opened early may have
  // no items yet; re-run when the list fills while focus is still on the
  // trigger so Arrow keys reach the menu without closing and reopening it.
  const hasRecentItems = recentConversations.length > 0;
  useEffect(() => {
    if (!recentOpen || !hasRecentItems) return;
    const focusInsideMenu = recentMenuRef.current?.contains(document.activeElement) ?? false;
    if (focusInsideMenu) return;
    // On a late hydration the user may have tabbed elsewhere (e.g. to Close)
    // while the empty menu sat open; only pull focus in when it is still on
    // the trigger (or nowhere), never away from another control.
    const active = document.activeElement;
    const focusIsParked = active === null || active === document.body;
    if (!focusIsParked && active !== recentTriggerRef.current) return;
    recentMenuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [recentOpen, hasRecentItems]);

  // Escape and outside pointer presses dismiss the menu only — never the dock.
  useEffect(() => {
    if (!recentOpen) return;
    function onPointerDown(event: PointerEvent) {
      const container = recentContainerRef.current;
      if (container && event.target instanceof Node && !container.contains(event.target)) {
        setRecentOpen(false);
      }
    }
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setRecentOpen(false);
      recentTriggerRef.current?.focus();
    }
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [recentOpen]);

  const onMenuKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const items = Array.from(
      recentMenuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (items.length === 0) return;
    const activeIndex = items.findIndex((item) => item === document.activeElement);
    // Focus can sit on the menu container itself (it is the keydown target when
    // no item holds focus); Down should then enter at the top, Up at the bottom.
    if (activeIndex === -1) {
      if (event.key === 'ArrowDown') {
        items[0]?.focus();
      } else {
        items[items.length - 1]?.focus();
      }
      return;
    }
    const step = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (activeIndex + step + items.length) % items.length;
    items[nextIndex]?.focus();
  }, []);

  useEffect(() => {
    if (!groundingResearchId) {
      setGroundingTitle(null);
      return;
    }
    const cached = titleCache.current.get(groundingResearchId);
    if (cached) {
      setGroundingTitle(cached);
      return;
    }
    let cancelled = false;
    setGroundingTitle(null);
    void (async () => {
      try {
        const response = await api.getResearch(groundingResearchId);
        if (cancelled) return;
        titleCache.current.set(groundingResearchId, response.item.title);
        setGroundingTitle(response.item.title);
      } catch {
        if (!cancelled) setGroundingTitle(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, groundingResearchId]);

  if (!open) return null;

  // A conversation with no messages is already "new" — starting another would
  // only discard the empty one. Prefer the panel's live count so the control
  // tracks in-memory transcripts that were never persisted.
  let messageCount = activeConversation.messages.length;
  if (liveMessages && liveMessages.conversationId === activeConversation.id) {
    messageCount = liveMessages.count;
  }
  const conversationIsEmpty = messageCount === 0;

  let emptyState: ReactNode = null;
  if (recentConversations.length === 0) {
    emptyState = <p className="px-3 py-2 text-sm text-muted-foreground">No recent conversations</p>;
  }

  let recentMenu: ReactNode = null;
  if (recentOpen) {
    recentMenu = (
      <div
        ref={recentMenuRef}
        role="menu"
        aria-label="Recent conversations"
        tabIndex={-1}
        onKeyDown={onMenuKeyDown}
        className="absolute top-full right-0 z-10 mt-2 w-72 rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-xl"
      >
        {emptyState}
        {recentConversations.map((conversation) => {
          const isActive = conversation.id === activeConversation.id;
          return (
            <button
              key={conversation.id}
              type="button"
              role="menuitem"
              aria-current={isActive ? 'true' : undefined}
              onClick={() => {
                switchConversation(conversation.id);
                setRecentOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left hover:bg-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring aria-[current]:bg-muted"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-foreground">{conversation.title}</span>
                <span className="block text-xs text-muted-foreground">
                  {relativeDate(conversation.updatedAt)}
                </span>
              </span>
              <ActiveConversationMarker active={isActive} />
            </button>
          );
        })}
      </div>
    );
  }

  let groundingPill: ReactNode = null;
  if (groundingResearchId && groundingTitle) {
    groundingPill = (
      <div className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs text-primary">
        <Anchor aria-hidden="true" className="size-3 shrink-0" />
        <span className="min-w-0 truncate">Grounded in: {groundingTitle}</span>
      </div>
    );
  }

  return (
    <aside className="fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-border bg-background lg:max-w-[26rem]">
      <header className="space-y-3 border-b border-border/70 px-4 py-3">
        <div className="flex items-center gap-1">
          <h2 className="min-w-0 flex-1 text-base font-semibold text-foreground">Ask Almanac</h2>
          <button
            type="button"
            aria-label="New conversation"
            title="New conversation"
            disabled={conversationIsEmpty}
            onClick={() => {
              newConversation();
              setRecentOpen(false);
            }}
            className={HEADER_BUTTON_CLASS}
          >
            <SquarePen aria-hidden="true" className="size-[18px]" />
          </button>
          <div className="relative" ref={recentContainerRef}>
            <button
              type="button"
              ref={recentTriggerRef}
              aria-label="Recent conversations"
              title="Recent conversations"
              aria-expanded={recentOpen}
              aria-haspopup="menu"
              onClick={() => {
                refreshConversations();
                setRecentOpen((value) => !value);
              }}
              className={HEADER_BUTTON_CLASS}
            >
              <History aria-hidden="true" className="size-[18px]" />
            </button>
            {recentMenu}
          </div>
          <button
            type="button"
            aria-label="Close assistant"
            title="Close assistant"
            onClick={close}
            className={HEADER_BUTTON_CLASS}
          >
            <X aria-hidden="true" className="size-[18px]" />
          </button>
        </div>
        {groundingPill}
      </header>
      <AssistantPanel
        key={activeConversation.id}
        conversation={activeConversation}
        groundingResearchId={groundingResearchId}
        pageContext={pageContext}
        seed={seed}
        storageScope={storageScope}
        onSaved={refreshConversations}
        onMessagesChanged={onPanelMessagesChanged}
      />
    </aside>
  );
}
